import React from 'react';
import { useState, useEffect, useRef } from 'react';
import { TbProgressCheck } from "react-icons/tb";

import './About.css';


function About({ startYear = 2017 }) {

    const [isVisible, setIsVisible] = useState(false);
    const sectionRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.1 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => observer.disconnect();
    }, []);

    const currentYear = new Date().getFullYear();
    const yearsExperience = currentYear - startYear;

  return (
    <section id="about" className="py-20 bg-white" ref={sectionRef}>
        <div className="max-w-6xl mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-6 items-center">
                <div className={`fade-in ${isVisible ? 'visible' : ''}`}>
                    <h2 className="text-4xl font-bold text-gray-800 mb-6">Strategic Project Leadership</h2>
                    <p className="text-lg text-gray-600 mb-6">
                        Results-driven professional with 8+ years of experience leading cross-functional teams and delivering complex initiatives across energy, construction, and eCommerce sectors.
                    </p>
                    <p className="text-lg text-gray-600 mb-8">
                        I specialize in transforming organizational challenges into successful outcomes through strategic planning, stakeholder alignment, and data-driven decision-making.
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <h3 className="font-semibold text-gray-800 mb-3">Core Competencies</h3>
                            <ul className="text-gray-600 space-y-2">
                                <li className="flex items-center"><span className="w-2 h-2 bg-emerald-500 rounded-full mr-3"></span>Agile & Waterfall Methodologies</li>
                                <li className="flex items-center"><span className="w-2 h-2 bg-emerald-500 rounded-full mr-3"></span>Risk Management & Mitigation</li>
                                <li className="flex items-center"><span className="w-2 h-2 bg-emerald-500 rounded-full mr-3"></span>Stakeholder Management</li>
                                <li className="flex items-center"><span className="w-2 h-2 bg-emerald-500 rounded-full mr-3"></span>Cost & Schedule Control</li>
                            </ul>
                        </div>
                        <div>
                            <h3 className="font-semibold text-gray-800 mb-3">Industry Experience</h3>
                            <ul className="text-gray-600 space-y-2">
                                <li className="flex items-center"><span className="w-2 h-2 bg-blue-500 rounded-full mr-3"></span>Industrial Energy</li>
                                <li className="flex items-center"><span className="w-2 h-2 bg-blue-500 rounded-full mr-3"></span>Greenfield/Brownfield Construction</li>
                                <li className="flex items-center"><span className="w-2 h-2 bg-blue-500 rounded-full mr-3"></span>Enterprise Software</li>
                                <li className="flex items-center"><span className="w-2 h-2 bg-blue-500 rounded-full mr-3"></span>Digital Transformation</li>
                            </ul>
                        </div>
                    </div>
                </div>
                <div className={`fade-in ${isVisible ? 'visible' : ''}`}>
                    <div className="space-y-6">
                        <div className="bg-yellow-50 rounded-2xl p-6 border border-yellow-200">
                            <div className="flex items-center mb-4">
                                <div className="w-12 h-12 bg-yellow-600 rounded-full flex items-center justify-center mr-4">
                                    {/* <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                                    </svg> */}
                                    <TbProgressCheck className="text-white w-6 h-6"/>
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-gray-800">PMP In Progress</h3>
                                    <p className="text-gray-600">Project Management Professional</p>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                    <span className="font-semibold text-gray-800">Certification ID:</span>
                                    <div className="text-gray-600">TBD</div>
                                </div>
                                <div>
                                    <span className="font-semibold text-gray-800">Target Completion Date:</span>
                                    <div className="text-gray-600">Dec 2025</div>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="metric-card rounded-xl p-4 border border-emerald-200">
                                <div className="text-2xl font-bold text-emerald-700">{yearsExperience}+</div>
                                <div className="text-sm text-gray-600">Years Experience</div>
                            </div>
                            <div className="metric-card rounded-xl p-4 border border-emerald-200">
                                <div className="text-2xl font-bold text-emerald-700">90+</div>
                                <div className="text-sm text-gray-600">Team Members Led</div>
                            </div>
                        </div>

                        <div className="bg-gray-50 rounded-xl p-6">
                            <h4 className="font-semibold text-gray-800 mb-3">Skills Acquired</h4>
                            <p>
                                Data Analysis, Change Management, Risk Mitigation, Strategic Planning, SaaS/PaaS, PLM, GTM, Agile, SCRUM, DevOps, SAP,
                                Azure, Excel, Macros, VBA, BI Tools, Civil 3D, MicroStation, QA/QC, MS Projects, P6, Jira, JS, React, Node,
                                Python, API Integration, CI/CD, Kubernetes, GCP, LLM, Prompt Engineering, GenAI, OpenAI, GitLab, SQL, SDKs, SEO, Google Analytics, SEMRush, Datadog, Figma, Lucid
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
  );
}

export default About;